import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { StickyNote } from "lucide-react";

export const Route = createFileRoute("/_authenticated/my-notes")({
  component: MyNotesPage,
});

type NoteResponse = "acknowledged" | "completed" | "need_clarification";

interface Note {
  id: string;
  content: string;
  isReadByWorker: boolean;
  response: NoteResponse | null;
  respondedAt: string | null;
  createdAt: string;
  authorName: string;
}

const RESPONSES: NoteResponse[] = ["acknowledged", "completed", "need_clarification"];

const RESPONSE_LABELS: Record<NoteResponse, string> = {
  acknowledged: "Acknowledged",
  completed: "Completed",
  need_clarification: "Need Clarification",
};

const RESPONSE_STYLES: Record<NoteResponse, string> = {
  acknowledged: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 ring-blue-200 dark:ring-blue-700",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 ring-green-200 dark:ring-green-700",
  need_clarification: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 ring-amber-200 dark:ring-amber-700",
};

function formatWhen(iso: string): string {
  // D1 stores "YYYY-MM-DD HH:MM:SS" (UTC, no zone); treat it as UTC.
  const d = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function MyNotesPage() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["my-notes"],
    queryFn: () => apiGet<{ data: Note[] }>("/api/me/notes"),
  });

  const notes = [...(data?.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const unreadCount = notes.filter((n) => !n.isReadByWorker).length;
  const awaitingCount = notes.filter((n) => !n.response).length;

  const markAllRead = useMutation({
    mutationFn: () => apiPost("/api/me/notes/mark-read"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-notes"] }),
  });

  // Opening the page counts as reading everything on it.
  useEffect(() => {
    if (unreadCount > 0 && !markAllRead.isPending) markAllRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);

  const respond = useMutation({
    mutationFn: ({ id, response }: { id: string; response: NoteResponse }) =>
      apiPost(`/api/me/notes/${id}/respond`, { response }),
    onSuccess: () => {
      toast.success("Response sent to your admin");
      queryClient.invalidateQueries({ queryKey: ["my-notes"] });
    },
    onError: (err: Error) => toast.error(err.message || "Could not send your response"),
  });

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="pt-2">
        <h1 className="text-2xl font-bold text-text-primary">My Messages</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Messages from your admins and supervisors, and your replies. You can respond here; only admins and supervisors can send them.
        </p>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      )}

      {isError && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">
            Could not load your messages. Please try again.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && notes.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <StickyNote className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">No messages yet</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Messages from your admins and supervisors will show up here.
            </p>
          </CardContent>
        </Card>
      )}

      {notes.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {notes.length} note{notes.length !== 1 ? "s" : ""}
          {awaitingCount > 0 ? ` · ${awaitingCount} waiting for your response` : " · all responded"}
        </p>
      )}

      <div className="space-y-3">
        {notes.map((n) => (
          <Card key={n.id} className={!n.response ? "border-blue-400/40" : undefined}>
            <CardContent className="py-3 px-4 space-y-2.5">
              <div className="flex items-start justify-between gap-2">
                <p className="text-[11px] text-muted-foreground">
                  {n.authorName} · {formatWhen(n.createdAt)}
                </p>
                {n.response ? (
                  <span className={`shrink-0 text-[11px] font-medium px-2.5 py-0.5 rounded-full ring-1 ${RESPONSE_STYLES[n.response]}`}>
                    {RESPONSE_LABELS[n.response]}
                  </span>
                ) : (
                  <span className="shrink-0 text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300">
                    Awaiting your response
                  </span>
                )}
              </div>

              <p className="text-sm text-foreground whitespace-pre-wrap">{n.content}</p>

              {n.response && n.respondedAt && (
                <p className="text-[11px] text-muted-foreground">
                  You replied "{RESPONSE_LABELS[n.response]}" on {formatWhen(n.respondedAt)}
                </p>
              )}

              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {RESPONSES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => respond.mutate({ id: n.id, response: r })}
                    disabled={respond.isPending || n.response === r}
                    className={`text-[11px] font-medium px-2.5 py-1 rounded-full ring-1 transition-opacity disabled:opacity-40 ${RESPONSE_STYLES[r]}`}
                  >
                    {n.response ? (n.response === r ? `${RESPONSE_LABELS[r]} ✓` : `Change to ${RESPONSE_LABELS[r]}`) : RESPONSE_LABELS[r]}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
